import { fmtEur, fmtPct, BRACKET_COLORS } from "@/lib/format";

const Line = ({ label, value, testId, bold, muted, sign }) => (
  <div className={`flex items-center justify-between py-2 border-b border-border/60 last:border-0 ${bold ? "font-semibold text-foreground" : muted ? "text-muted-foreground" : "text-foreground/80"}`}>
    <span className="text-sm">{label}</span>
    <span className={`font-mono text-sm ${bold ? "text-base" : ""}`} data-testid={testId}>{sign}{fmtEur(value)}</span>
  </div>
);

export const TaxBreakdown = ({ result }) => {
  if (!result) return null;
  const maxBase = Math.max(1, ...result.tranches.map((t) => t.base));
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="tax-calculator-result-card">
      <div className="bg-card border border-border rounded-md p-5">
        <div className="eyebrow mb-4">Décomposition par tranche · barème {result.annee}{result.mois < 12 ? ` (${result.mois} mois)` : ""}</div>
        <div className="space-y-4">
          {result.tranches.map((t, i) => (
            <div key={t.code} data-testid={`bracket-row-${t.code}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: BRACKET_COLORS[i] }} />
                  <span className="font-medium text-foreground">{t.libelle}</span>
                  {t.seuil != null && <span className="text-xs text-muted-foreground/70">jusqu'à {fmtEur(t.seuil)}</span>}
                </div>
                <span className="font-mono text-foreground">{fmtEur(t.taxe)}</span>
              </div>
              <div className="h-2 bg-muted rounded-sm overflow-hidden">
                <div className="h-full transition-[width] duration-500" style={{ width: `${(t.base / maxBase) * 100}%`, background: BRACKET_COLORS[i] }} />
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">Base : {fmtEur(t.base)}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-border flex justify-between text-sm">
          <span className="text-muted-foreground">Effectif · Masse salariale</span>
          <span className="font-mono" data-testid="result-payroll">{result.effectif} · {fmtEur(result.masse_salariale)}</span>
        </div>
        {result.exonerations_apprentis > 0 && (
          <div className="mt-2 flex justify-between text-sm" data-testid="result-apprentis">
            <span className="text-muted-foreground">Exonération apprentis ({result.nb_apprentis}, {result.regle_apprentis === "totale" ? "totale — effectif ≤ 10" : `11 % du SMIC · ${fmtEur(result.plafond_exoneration_apprenti)}`})</span>
            <span className="font-mono text-emerald-700 dark:text-emerald-400">− {fmtEur(result.exonerations_apprentis)}</span>
          </div>
        )}
        {result.exonerations_apprentis > 0 && (
          <div className="mt-1 flex justify-between text-sm font-medium">
            <span className="text-foreground/80">Base imposable</span>
            <span className="font-mono" data-testid="result-taxable-base">{fmtEur(result.base_imposable)}</span>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-md p-5">
        <div className="eyebrow mb-3">Liquidation</div>
        <Line label="Taxe brute" value={result.taxe_brute} testId="result-tax-gross" />
        {result.rapport_assujettissement !== 100 && (
          <Line label={`Après rapport d'assujettissement (${fmtPct(result.rapport_assujettissement)})`} value={result.taxe_apres_rapport} testId="result-after-ratio" />
        )}
        {result.is_association && (
          <Line label={`Abattement associations (disponible ${fmtEur(result.abattement_disponible)})`} value={result.abattement_association} testId="result-abatement" sign="− " muted />
        )}
        {result.annuel && result.franchise > 0 && <Line label="Franchise (taxe ≤ 1 200 €)" value={result.franchise} testId="result-franchise" sign="− " muted />}
        {result.annuel && result.decote > 0 && <Line label="Décote 0,75 × (2 040 − taxe)" value={result.decote} testId="result-decote" sign="− " muted />}
        <div className="mt-3 rounded-md bg-ink text-white px-4 py-4 border-l-4 border-brand-500 flex items-center justify-between">
          <span className="font-heading font-semibold">Taxe nette due</span>
          <span className="font-mono text-2xl font-semibold" data-testid="result-tax-net">{fmtEur(result.taxe_nette)}</span>
        </div>
        {!result.annuel && <p className="text-xs text-muted-foreground mt-3">Acompte provisionnel : la franchise et la décote sont appliquées lors de la régularisation annuelle (2502-SD).</p>}
      </div>
    </div>
  );
};
